import * as THREE from 'three';
import type { Item } from '../../core/hp/types';
import { fullGeometryOf } from './modeVisibility';

interface Member { mesh: THREE.Mesh; key: string }
interface Canonical { node: THREE.Object3D; members: Member[] }
interface Cohort { members: Set<Member>; batch?: THREE.InstancedMesh; release?: () => void; mapping: THREE.Mesh[] }

/** Shared opaque primitives, bounded in space. Canonical nodes own editing and assets. */
export class InstancedCohorts {
  readonly root = new THREE.Group();
  private canonical = new Map<string, Canonical>();
  private groups = new Map<string, Cohort>();
  private dirty = new Set<string>();
  version = 0;

  constructor(private readonly retainResources: (root: THREE.Object3D) => () => void = () => () => {}) {
    this.root.name = 'instanced-cohorts';
  }

  update(node: THREE.Object3D, item: Item, excluded = false): void {
    const previous = this.canonical.get(node.name);
    if (previous) this.detach(previous);
    const record: Canonical = { node, members: [] };
    this.canonical.set(node.name, record);
    if (excluded || !node.userData.real || node.userData.generated || item.mirrored || item.scale.some(scale=>scale<0)) return;
    node.updateWorldMatrix(true, true);
    const cell = `${Math.floor(item.position.x / 4)},${Math.floor(item.position.z / 4)}`;
    node.traverse(object => {
      const mesh = object as THREE.Mesh, material = mesh.material as THREE.MeshStandardMaterial;
      if (!mesh.isMesh || (mesh as THREE.SkinnedMesh).isSkinnedMesh || (mesh as THREE.InstancedMesh).isInstancedMesh
        || mesh.morphTargetInfluences?.length || Array.isArray(mesh.material) || !material?.isMeshStandardMaterial
        || material.transparent || material.opacity < 1 || material.alphaTest > 0 || material.alphaHash
        || (material as THREE.MeshPhysicalMaterial).transmission > 0 || fullGeometryOf(mesh) !== mesh.geometry
        || !visible(mesh) || !positiveOrthogonal(mesh.matrixWorld)) return;
      const key = [mesh.geometry.uuid, material.uuid, mesh.castShadow, mesh.receiveShadow,
        mesh.renderOrder, mesh.layers.mask, mesh.frustumCulled, cell].join('|');
      const member: Member = { mesh, key };
      let group = this.groups.get(key);
      if (!group) this.groups.set(key, group = { members: new Set(), mapping: [] });
      group.members.add(member); record.members.push(member); this.dirty.add(key);
    });
  }

  remove(node: THREE.Object3D): void {
    const record = this.canonical.get(node.name);
    // A late abandoned hydration result can have the same name as a live replacement.
    if (record?.node !== node) return;
    this.detach(record); this.canonical.delete(node.name);
  }

  private detach(record: Canonical): void {
    for (const member of record.members) {
      if (member.mesh.userData.cohortHidden) { member.mesh.visible = true; delete member.mesh.userData.cohortHidden; }
      this.groups.get(member.key)?.members.delete(member); this.dirty.add(member.key);
    }
  }

  /** Only changed spatial/material buckets allocate an instance buffer. */
  flush(): void {
    if (!this.dirty.size) return;
    for (const key of this.dirty) {
      const group = this.groups.get(key); if (!group) continue;
      const members = [...group.members];
      let next: THREE.InstancedMesh | undefined, release: (() => void) | undefined;
      if (members.length >= 4) {
        const first = members[0].mesh;
        next = new THREE.InstancedMesh(first.geometry, first.material, members.length);
        next.name = key; next.userData.instancedCohort = true;
        next.castShadow = first.castShadow; next.receiveShadow = first.receiveShadow;
        next.renderOrder = first.renderOrder; next.layers.mask = first.layers.mask; next.frustumCulled = first.frustumCulled;
        members.forEach(({mesh}, index) => {
          mesh.updateWorldMatrix(true, false); next!.setMatrixAt(index, mesh.matrixWorld);
          mesh.visible = false; mesh.userData.cohortHidden = true;
        });
        next.instanceMatrix.needsUpdate = true; next.computeBoundingBox(); next.computeBoundingSphere();
        release = this.retainResources(next); this.root.add(next);
      } else for (const {mesh} of members) {
        if (mesh.userData.cohortHidden) { mesh.visible = true; delete mesh.userData.cohortHidden; }
      }
      group.batch?.removeFromParent(); group.batch?.dispose(); group.release?.();
      group.batch = next; group.release = release; group.mapping = next ? members.map(member=>member.mesh) : [];
      if (!members.length) this.groups.delete(key);
    }
    this.dirty.clear(); this.version++;
  }

  /** Return the canonical mesh so item identity and named material slots stay exact. */
  pick(ray: THREE.Raycaster): THREE.Intersection | undefined {
    this.flush();
    if (!this.root.visible) return undefined;
    const hits = ray.intersectObjects(this.root.children, false);
    for (const hit of hits) {
      const source = this.groups.get(hit.object.name)?.mapping[hit.instanceId ?? -1];
      if (source && visible(source, true)) return { ...hit, object: source };
    }
    return undefined;
  }

  stats(): { cohortMeshes: number; cohortInstances: number } {
    let instances = 0;
    for (const group of this.groups.values()) instances += group.batch?.count ?? 0;
    return { cohortMeshes: this.root.children.length, cohortInstances: instances };
  }

  clear(): void {
    for (const record of this.canonical.values()) this.detach(record);
    for (const group of this.groups.values()) { group.batch?.removeFromParent(); group.batch?.dispose(); group.release?.(); }
    this.canonical.clear(); this.groups.clear(); this.dirty.clear(); this.version++;
  }
  dispose(): void { this.clear(); this.root.removeFromParent(); }
}

function visible(mesh: THREE.Object3D, ignoreCohort = false): boolean {
  for (let node: THREE.Object3D | null = mesh; node; node = node.parent)
    if (!node.visible && !(ignoreCohort && node === mesh && node.userData.cohortHidden)) return false;
  return true;
}

function positiveOrthogonal(matrix: THREE.Matrix4): boolean {
  if (matrix.determinant() <= 1e-12) return false;
  const x = new THREE.Vector3().setFromMatrixColumn(matrix, 0), y = new THREE.Vector3().setFromMatrixColumn(matrix, 1),
    z = new THREE.Vector3().setFromMatrixColumn(matrix, 2);
  // The instancing normal transform supports nonuniform TRS, but not hierarchy shear.
  return Math.abs(x.dot(y)) <= 1e-6*x.length()*y.length() && Math.abs(x.dot(z)) <= 1e-6*x.length()*z.length()
    && Math.abs(y.dot(z)) <= 1e-6*y.length()*z.length();
}
